import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, Avatar, IconButton, TextField,
  Stack, Badge, Divider, CircularProgress, Tooltip, Button,
  InputBase, Container, Link
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { sendMessage, getChats, setCurrentChat, deleteChat } from '../../actions/chat';
import { urlImg } from '../common/helpers';
import ModalCotizar from './ModalCotizar';
import ModalDetalleCotizar from './ModalDetalleCotizar';
import BotonAdjuntar from './BotonAdjuntar';
import VistaPreviaAdjuntos from './VistaPreviaAdjuntos';
import AdjuntosMensaje from './AdjuntosMensaje';
import { useAdjuntos } from './useAdjuntos';
import MenuChat from './MenuChat';
import AvisoBloqueo from './AvisoBloqueo';
import { MAX_TEXTO_CHAT } from './limites';
import { etiquetaDia, fechaEnLista, horaMensaje, mismoDia } from './fechas';

// Nombre legible del tema de un chat (servicio/página) y su categoría de respaldo
const temaTitulo = (chat) => chat?.tema?.nombre || chat?.tema?.categoria || '';
const temaCategoria = (chat) => (chat?.tema?.nombre && chat?.tema?.categoria) ? chat.tema.categoria : '';

// Envuelve en un enlace al perfil de la otra persona; sin ruta (datos incompletos) deja el contenido tal cual
const EnlacePerfil = ({ to, underline = 'none', sx, children }) => (
  to ? (
    <Tooltip title="Ver perfil" describeChild>
      <Link component={RouterLink} to={to} underline={underline} color="inherit" sx={sx}>
        {children}
      </Link>
    </Tooltip>
  ) : children
);

const ChatPage = () => {
  const dispatch = useDispatch();
  const { currentChat, messages, chats, loading, onlineUsers, presenciaRecibida } = useSelector(state => state.chat);
  const authUser = useSelector(state => state.auth.user || state.user.user); // Check both potential locations

  const getStrId = (id) => {
    if (!id) return '';
    if (typeof id === 'string') return id;
    if (typeof id === 'object' && id._id) return id._id.toString();
    return String(id);
  };

  const otherParticipant = currentChat?.participants?.find(p => getStrId(p._id) !== getStrId(authUser?._id));
  const isOnline = otherParticipant && onlineUsers.some(id => getStrId(id) === getStrId(otherParticipant._id));
  // Mismo criterio que las reservas y el widget: el proveedor ve la ficha del cliente y el cliente el perfil público del proveedor
  const soyProveedor = !!currentChat && getStrId(currentChat.provider) === getStrId(authUser?._id);
  const rutaPerfil = otherParticipant?._id
    ? `${soyProveedor ? '/user/cliente' : '/perfil'}/${getStrId(otherParticipant._id)}`
    : null;

  const [text, setText] = useState('');
  const [search, setSearch] = useState('');
  const messagesEndRef = useRef(null);
  const [openCotizar, setOpenCotizar] = useState(false);
  const [openQuoteDetail, setOpenQuoteDetail] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const adj = useAdjuntos();
  const [enviando, setEnviando] = useState(false);
  const { limpiar: limpiarAdjuntos } = adj;

  // Los archivos elegidos son de UNA conversación: al cambiar de chat se descartan para no mandarlos a otra persona
  useEffect(() => { limpiarAdjuntos(); }, [currentChat?._id, limpiarAdjuntos]);

  useEffect(() => {
    dispatch(getChats());
    dispatch({ type: 'TOGGLE_CHAT', payload: true }); // Asegurar que el estado isOpen sea true en pantalla completa

    return () => {
      dispatch({ type: 'TOGGLE_CHAT', payload: false }); // Limpiar al salir
    };
  }, [dispatch]);

  useEffect(() => {
    if (messages && messages.length > 0) {
      // Forzar scroll al fondo con timeout para asegurar que el DOM se actualizó
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 100);
    }
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    const archivos = adj.archivos;
    if (!currentChat || enviando || adj.procesando || (!text.trim() && archivos.length === 0)) return;
    const contenido = text;
    setText('');
    if (archivos.length === 0) {
      // Se espera el resultado: si el servidor lo rechaza (límite de velocidad, mensaje largo…) se muestra el motivo y no se pierde lo escrito
      const r = await dispatch(sendMessage(currentChat._id, contenido));
      if (r?.ok) adj.setError('');
      else {
        setText((actual) => actual || contenido);
        adj.setError(r?.error || 'No se pudo enviar el mensaje');
      }
      return;
    }
    // Con archivos la subida tarda: se espera el resultado y, si falla, se conserva todo para reintentar
    setEnviando(true);
    const r = await dispatch(sendMessage(currentChat._id, contenido, null, 'text', archivos));
    setEnviando(false);
    if (r?.ok) {
      adj.limpiar();
    } else {
      setText((actual) => actual || contenido);
      adj.setError(r?.error || 'No se pudo enviar el mensaje');
    }
  };

  // Safety check for chats array
  const safeChats = Array.isArray(chats) ? chats : [];

  const filteredChats = safeChats.filter(chat => {
    const other = chat.participants?.find(p => p._id !== authUser?._id);
    const fullName = `${other?.name || ''} ${other?.apellido || ''}`.toLowerCase();
    return fullName.includes(search.toLowerCase());
  });

  // Referencia para "Hoy" / "Ayer" en la lista y en los separadores de la conversación
  const ahora = new Date();

  return (
    <Container maxWidth="xl" sx={{ mt: 15, mb: 2, height: 'calc(100vh - 200px)' }}>
      <Paper elevation={0} sx={{ height: '100%', borderRadius: '20px', overflow: 'hidden', border: 1, borderColor: 'divider', display: 'flex' }}>

        {/* Sidebar: Chat List */}
        <Box sx={{ width: { xs: '80px', md: '350px' }, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', bgcolor: 'grey.50' }}>
          <Box sx={{ p: 3, display: { xs: 'none', md: 'block' } }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main', mb: 2 }}>Mensajes</Typography>
            <Paper
              elevation={0}
              sx={{
                p: '4px 12px', display: 'flex', alignItems: 'center', bgcolor: 'sutil', borderRadius: '12px'
              }}
            >
              <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
              <InputBase
                placeholder="Buscar"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ fontSize: '0.9rem' }}
              />
            </Paper>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 1, md: 2 } }}>
            {loading && safeChats.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress size={24} /></Box>
            ) : filteredChats.length > 0 ? (
              filteredChats.map(chat => {
                const other = chat.participants?.find(p => getStrId(p._id) !== getStrId(authUser?._id));
                const isActive = getStrId(currentChat?._id) === getStrId(chat._id);
                const avatar = other?.avatar ? (other.avatar.startsWith('http') ? other.avatar : `${urlImg}/users/${getStrId(other._id)}/${other.avatar}`) : '';
                const userIsOnline = other && onlineUsers.some(id => getStrId(id) === getStrId(other._id));

                return (
                  <Box
                    key={chat._id}
                    onClick={() => dispatch(setCurrentChat(chat))}
                    sx={{
                      p: 2, mb: 1, borderRadius: '16px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 2,
                      bgcolor: isActive ? 'white' : 'transparent',
                      boxShadow: isActive ? '0 4px 12px rgba(0,0,0,0.05)' : 'none',
                      '&:hover': { bgcolor: isActive ? 'white' : 'sutil' },
                      transition: 'all 0.2s'
                    }}
                  >
                    <Badge color="success" variant="dot" invisible={!userIsOnline} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                      <Avatar src={avatar} sx={{ width: 48, height: 48 }} />
                    </Badge>
                    <Box sx={{ flex: 1, minWidth: 0, display: { xs: 'none', md: 'block' } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }} noWrap>
                          {other?.name} {other?.apellido}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0, ml: 1, whiteSpace: 'nowrap' }}>
                          {fechaEnLista(chat.lastMessage?.createdAt, ahora)}
                        </Typography>
                      </Stack>
                      {temaTitulo(chat) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, my: 0.25 }}>
                          <LocalOfferOutlinedIcon sx={{ fontSize: 13, color: 'secondary.main', flexShrink: 0 }} />
                          <Typography variant="caption" noWrap sx={{ color: 'secondary.main', fontWeight: 600 }}>
                            {temaTitulo(chat)}
                          </Typography>
                        </Box>
                      )}
                      <Typography variant="caption" noWrap sx={{ color: isActive ? 'primary.main' : 'text.secondary', display: 'block', fontWeight: chat.unreadCount?.[authUser?._id] > 0 ? 700 : 400 }}>
                        {chat.lastMessage?.content || 'Sin mensajes'}
                      </Typography>
                    </Box>
                    {chat.unreadCount?.[authUser?._id] > 0 && (
                      <Badge badgeContent={chat.unreadCount[authUser._id]} color="primary" sx={{ display: { xs: 'none', md: 'block' } }} />
                    )}
                  </Box>
                );
              })
            ) : (
              <Box sx={{ textAlign: 'center', mt: 4, opacity: 0.5, display: { xs: 'none', md: 'block' } }}>
                <Typography variant="body2">No hay conversaciones</Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Main Chat Area (minWidth 0: sin él, en el celular la cabecera la hace más ancha que la pantalla y se corta la conversación) */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', bgcolor: 'white' }}>
          {currentChat ? (
            <>
              {/* Chat Header */}
              <Box sx={{ p: 2, px: { xs: 1.5, sm: 3 }, gap: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'sutil' }}>
                <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} useFlexGap alignItems="center" sx={{ minWidth: 0 }}>
                  {/* Foto y nombre llevan al perfil de la otra persona (en el celular la foto ya se ve en la lista de la izquierda) */}
                  <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
                    <EnlacePerfil to={rutaPerfil} sx={{ display: 'flex' }}>
                      <Avatar
                        src={currentChat.participants?.find(p => getStrId(p._id) !== getStrId(authUser?._id))?.avatar ? (currentChat.participants.find(p => getStrId(p._id) !== getStrId(authUser?._id)).avatar.startsWith('http') ? currentChat.participants.find(p => getStrId(p._id) !== getStrId(authUser?._id)).avatar : `${urlImg}/users/${getStrId(currentChat.participants.find(p => getStrId(p._id) !== getStrId(authUser?._id))._id)}/${currentChat.participants.find(p => getStrId(p._id) !== getStrId(authUser?._id)).avatar}`) : ''}
                        sx={{ width: 44, height: 44 }}
                      />
                    </EnlacePerfil>
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} useFlexGap alignItems="center" flexWrap="wrap">
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }} noWrap>
                        <EnlacePerfil to={rutaPerfil} underline="hover">
                          {otherParticipant?.name} {otherParticipant?.apellido}
                        </EnlacePerfil>
                      </Typography>
                      {temaTitulo(currentChat) && (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, bgcolor: (t) => alpha(t.palette.secondary.main, 0.12), color: 'secondary.main', px: 1, py: 0.25, borderRadius: '8px', maxWidth: '100%' }}>
                          <LocalOfferOutlinedIcon sx={{ fontSize: 13, flexShrink: 0 }} />
                          <Typography variant="caption" noWrap sx={{ fontWeight: 700 }}>
                            {temaTitulo(currentChat)}{temaCategoria(currentChat) ? ` · ${temaCategoria(currentChat)}` : ''}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                    {/* Hasta que el servidor manda quién está conectado no se sabe: mejor no decir nada */}
                    {presenciaRecibida && (
                      <Typography variant="caption" sx={{ color: isOnline ? 'success.oscuro' : 'text.secondary', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 6, height: 6, bgcolor: isOnline ? 'success.main' : 'text.secondary', borderRadius: '50%' }} />
                        {isOnline ? 'En línea' : 'Desconectado'}
                      </Typography>
                    )}
                  </Box>
                </Stack>
                <Stack direction="row" spacing={{ xs: 0, sm: 1 }}>
                  <Tooltip title="Eliminar conversación">
                    <IconButton onClick={() => {
                      if (window.confirm('¿Eliminar conversación?')) dispatch(deleteChat(currentChat._id));
                    }}>
                      <DeleteOutlineIcon sx={{ color: 'text.secondary' }} />
                    </IconButton>
                  </Tooltip>
                  <MenuChat chat={currentChat} nombre={otherParticipant?.name} />
                </Stack>
              </Box>

              {/* Messages Area */}
              <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, md: 4 }, bgcolor: 'grey.50' }}>
                {Array.isArray(messages) && messages.map((msg, index) => {
                  const getStrId = (id) => (typeof id === 'object' && id?._id ? id._id.toString() : String(id || ''));
                  const msgSenderId = getStrId(msg.sender);
                  const myId = getStrId(authUser?._id);
                  const isMe = msgSenderId === myId;

                  const sender = isMe ? authUser : currentChat.participants?.find(p => getStrId(p._id) === msgSenderId);
                  const avatar = sender?.avatar ? (sender.avatar.startsWith('http') ? sender.avatar : `${urlImg}/users/${sender._id}/${sender.avatar}`) : '';
                  // El primer mensaje de cada día lleva arriba el día en que se mandó ("Hoy", "Ayer", "9 de octubre"…)
                  const dia = index === 0 || !mismoDia(messages[index - 1].createdAt, msg.createdAt) ? etiquetaDia(msg.createdAt, ahora) : '';

                  return (
                    <React.Fragment key={msg._id || index}>
                    {dia && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                        <Divider sx={{ flex: 1 }} />
                        <Typography variant="caption" sx={{ px: 2, color: 'text.secondary', fontWeight: 600 }}>{dia}</Typography>
                        <Divider sx={{ flex: 1 }} />
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', mb: 3 }}>
                      <Stack direction={isMe ? 'row-reverse' : 'row'} spacing={1.5} alignItems="flex-end">
                        <EnlacePerfil to={isMe ? null : rutaPerfil} sx={{ display: 'flex' }}>
                          <Avatar src={avatar} sx={{ width: 32, height: 32 }} />
                        </EnlacePerfil>
                        {msg.type === 'quote' ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
                            <Button
                              variant="contained"
                              onClick={() => {
                                setSelectedQuoteId(msg.quote?._id || msg.quote);
                                setOpenQuoteDetail(true);
                              }}
                              sx={{
                                bgcolor: 'primary.main', color: 'white', borderRadius: '24px',
                                px: 4, py: 1.2, textTransform: 'none', fontWeight: 700,
                                '&:hover': { bgcolor: 'primary.dark' },
                                boxShadow: (t) => `0 4px 12px ${alpha(t.palette.primary.main, 0.15)}`
                              }}
                            >
                              Abrir cotización
                            </Button>
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.5, fontSize: '0.65rem' }}>
                              {horaMensaje(msg.createdAt)}
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{
                            maxWidth: { xs: '250px', md: '450px' }, p: 2, borderRadius: isMe ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                            bgcolor: isMe ? ((t) => alpha(t.palette.secondary.main, 0.4)) : 'white', color: 'text.primary',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                            position: 'relative'
                          }}>
                            {!msg.soloAdjuntos && (
                              <Typography variant="body2" sx={{ fontSize: '0.9rem', lineHeight: 1.5, wordBreak: 'break-word' }}>{msg.content}</Typography>
                            )}
                            <AdjuntosMensaje adjuntos={msg.adjuntos} />
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.5, textAlign: 'right', fontSize: '0.65rem' }}>
                              {horaMensaje(msg.createdAt)}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </Box>

              {/* Input Area (si yo bloqueé a la otra persona, en su lugar va el aviso para desbloquear) */}
              {currentChat.bloqueadoPorMi ? (
                <AvisoBloqueo chatId={currentChat._id} nombre={otherParticipant?.name} />
              ) : (
              <Box component="form" onSubmit={handleSend} sx={{ p: 2, borderTop: 1, borderColor: 'sutil' }}>
                <VistaPreviaAdjuntos archivos={adj.archivos} error={adj.error} onQuitar={adj.quitar} enviando={enviando} procesando={adj.procesando} />
                {/* En el celular, si los botones no caben junto al campo, bajan a otra línea y el campo conserva su ancho */}
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" justifyContent="flex-end">
                  <BotonAdjuntar onArchivos={adj.agregar} disabled={enviando} />
                  <TextField
                    fullWidth
                    placeholder="Escribe un mensaje..."
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    autoComplete="off"
                    inputProps={{ maxLength: MAX_TEXTO_CHAT }}
                    size="small"
                    sx={{
                      flex: '1 1 120px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px', bgcolor: 'grey.50',
                        '& fieldset': { border: 'none' }
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={enviando || adj.procesando || (!text.trim() && adj.archivos.length === 0)}
                    sx={{ borderRadius: '10px', px: 2, textTransform: 'none', boxShadow: 'none' }}
                  >
                    {enviando ? 'Enviando…' : 'Enviar'}
                  </Button>
                  {getStrId(authUser?._id) === getStrId(currentChat?.provider) && (
                    <Button
                      variant="contained" color="secondary"
                      onClick={() => setOpenCotizar(true)}
                      sx={{ borderRadius: '10px', px: 2, textTransform: 'none', boxShadow: 'none' }}
                    >
                      Cotizar
                    </Button>
                  )}
                </Stack>
              </Box>
              )}
            </>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', opacity: 0.3 }}>
              <Avatar sx={{ width: 100, height: 100, bgcolor: 'sutil', mb: 2 }}>
                <SendIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
              </Avatar>
              <Typography variant="h6">Selecciona un mensaje</Typography>
            </Box>
          )}
        </Box>
      </Paper>
      <ModalCotizar open={openCotizar} onClose={() => setOpenCotizar(false)} currentChat={currentChat} />
      <ModalDetalleCotizar open={openQuoteDetail} onClose={() => setOpenQuoteDetail(false)} quoteId={selectedQuoteId} chatId={currentChat?._id} />
    </Container>
  );
};

export default ChatPage;
